// The commercial hub (TX-US-0028): where a BD reports their day.
//
// Built for a phone held in one hand. One column: the BD addressed by name and asked for the
// day's update, the notes, then Record - the largest control, in the lower half of the screen
// where a thumb rests - the files under it, and Submit anchored to the bottom edge. What was
// reported before is below, a scroll away.
//
// Nothing here is saved by Submit alone. A recording goes to storage the moment it stops and a
// file the moment it is chosen; the notes are saved when the field is left. Submit seals the
// session: after it the update is read-only, and the next thing the BD does opens a new one.

import { useEffect, useState } from 'react';

import { confirmHubAttachmentUpload, requestHubAttachmentUpload } from '../api.js';
import useAttachmentUploads from '../deliveries/attachments/useAttachmentUploads.js';
import { useMe } from '../MeContext.js';
import Icon from '../ui/Icon.jsx';
import { notify } from '../ui/notify.jsx';
import { greeting } from './greeting.js';
import HubFiles from './HubFiles.jsx';
import { MAX_ATTACHMENTS, roomFor, validateHubFile } from './hubFiles.js';
import HubHistory from './HubHistory.jsx';
import { dayLabel } from './localDay.js';
import RecordControl from './RecordControl.jsx';
import useHubSession, { SENDING } from './useHubSession.js';

const MAX_TEXT_LENGTH = 20_000;

const HUB_UPLOADS = {
  request: requestHubAttachmentUpload,
  confirm: confirmHubAttachmentUpload,
  validate: validateHubFile,
};

export default function HubHome() {
  const me = useMe();
  const hub = useHubSession(me.email);
  const uploads = useAttachmentUploads(HUB_UPLOADS);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [latest, setLatest] = useState(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  const { draft, ensureDraft, setDraft, setError } = hub;
  const sessionId = draft?.session_id ?? null;

  // The notes take a draft's text when one arrives with some - found on load - and empty when the
  // draft goes. A draft opened by recording or attaching has none, and must not wipe what the BD
  // has typed and not yet left the field.
  const draftText = draft?.text ?? '';
  useEffect(() => {
    if (!sessionId) setText('');
    else if (draftText) setText(draftText);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on which draft, not its text
  }, [sessionId]);

  // A chosen file uploads at once, opening the draft if this is the first thing the BD did.
  const { pending, draining, drain } = uploads;
  useEffect(() => {
    if (pending === 0 || draining) return;
    ensureDraft()
      .then((session) => drain(session.session_id))
      .then(({ uploaded }) => {
        if (uploaded.length > 0) setDraft(uploaded[uploaded.length - 1]);
      })
      .catch((err) => setError(err.message));
  }, [pending, draining, drain, ensureDraft, setDraft, setError]);

  if (hub.phase === 'loading') return <p className="empty">Loading…</p>;
  if (hub.phase === 'error') return <p className="error">{hub.error}</p>;

  const files = draft?.attachments ?? [];
  const room = roomFor(files.length, uploads.items.length);
  const busy = hub.sending === SENDING.sending || draining || pending > 0 || submitting;
  const hasContent = Boolean(text.trim() || draft?.recording || files.length > 0);

  const addFiles = (chosen) => {
    const fitting = Array.from(chosen).slice(0, room);
    if (fitting.length < chosen.length) {
      setError(`An update holds up to ${MAX_ATTACHMENTS} files.`);
    }
    uploads.add(fitting);
  };

  const submit = async () => {
    setSubmitting(true);
    const submitted = await hub.submit(text);
    setSubmitting(false);
    if (!submitted) return;
    setLatest(submitted);
    notify('Update submitted');
  };

  // Two presses: the first arms the button, the second discards.
  const discard = async () => {
    if (!confirmingDiscard) {
      setConfirmingDiscard(true);
      return;
    }
    setConfirmingDiscard(false);
    if (await hub.discard()) setText('');
  };

  return (
    <div className="hub">
      <section className="hub-compose" aria-label="Today's update">
        <header className="hub-head">
          <p className="hub-day">{dayLabel()}</p>
          <p className={`hub-status${hub.submittedToday ? ' reported' : ''}`}>
            {hub.submittedToday && <Icon name="check" />}
            {hub.submittedToday ? 'Reported today' : 'Not reported yet today'}
          </p>
          <h1>{greeting(me, hub.submittedToday)}</h1>
        </header>

        <label className="hub-notes">
          <span>Notes</span>
          <textarea
            value={text}
            maxLength={MAX_TEXT_LENGTH}
            rows={3}
            placeholder="Type your update, or record it below"
            onChange={(event) => setText(event.target.value)}
            onBlur={() => hub.saveText(text)}
          />
        </label>

        <RecordControl hub={hub} />

        <HubFiles
          sessionId={sessionId}
          files={files}
          uploads={uploads}
          room={room}
          onAdd={addFiles}
          onRemove={hub.removeAttachment}
          onError={setError}
        />

        {hub.error && <p className="error" role="alert">{hub.error}</p>}

        <div className="hub-actions">
          {draft && (
            <button type="button" className={`hub-discard${confirmingDiscard ? ' confirming' : ''}`}
                    onClick={discard} onBlur={() => setConfirmingDiscard(false)} disabled={busy}>
              {confirmingDiscard ? 'Tap again to discard' : 'Discard'}
            </button>
          )}
          <button type="button" className="primary hub-submit" onClick={submit}
                  disabled={busy || !hasContent}>
            {submitting ? 'Submitting…' : 'Submit update'}
          </button>
        </div>
      </section>

      <HubHistory latest={latest} />
    </div>
  );
}
